import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Printer, Share2, Save, ThumbsUp, ThumbsDown, Sparkles, Car, Home } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { savePlan, sharePlan, getPlanByToken } from "@/lib/plan.functions";
import { submitPlanRating } from "@/lib/feedback.functions";
import { FeedbackModal } from "@/components/SiteFeedback";
import { EventCard } from "@/components/EventCard";
import { buildSeoMeta, SITE_URL } from "@/lib/seo";
import { getScheduleLabel } from "@/lib/schedule-label";
import {
  optimizeItinerary,
  flattenOrder,
  formatClock,
  formatDuration,
  type OptimizedItinerary,
} from "@/lib/itinerary-optimize";
import { extractZipFromAddress, type LatLng, LWR_CENTER } from "@/lib/distance";
import { supabase } from "@/integrations/supabase/client";
import { geocodeZip } from "@/lib/geocode.functions";
import { useAuth } from "@/lib/auth-context";
import { upsertMyPlan } from "@/lib/user-data.functions";

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function formatDayHeader(iso: string): string {
  const d = Date.parse(iso + "T12:00:00Z");
  if (!Number.isFinite(d)) return "";
  const dt = new Date(d);
  return `${WEEKDAY_NAMES[dt.getUTCDay()]} ${MONTH_NAMES[dt.getUTCMonth()]} ${dt.getUTCDate()}`;
}

export const Route = createFileRoute("/plan")({
  head: () => buildSeoMeta({
    title: "Plan Your Day In Lakewood Ranch — RanchPlanner",
    description:
      "Build a personalized itinerary of things to do in and around Lakewood Ranch, FL. Save, share, and go.",
    path: "/plan",
  }),
  component: ItineraryPage,
});

interface SavedEvent {
  id: string;
  title: string;
  description: string;
  category: string;
  venue_name: string;
  venue_address?: string;
  /** Optional override for the title link — see EventCardData. */
  map_url?: string;
  estimated_duration: string;
  cost_level: string;
  cost_details: string;
  drive_minutes_from_lwr: number;
  source_url: string;
  day_of_week?: string;
  schedule_notes?: string;
  start_date?: string | null;
  end_date?: string | null;
  crowd_level?: string;
  weather_sensitivity?: number;
  indoor_outdoor?: string;
}

function ItineraryPage() {
  const [events, setEvents] = useState<SavedEvent[]>([]);
  const [itinerary, setItinerary] = useState<OptimizedItinerary<SavedEvent> | null>(null);
  const [zipCoords, setZipCoords] = useState<Map<string, LatLng>>(new Map());
  const [arranging, setArranging] = useState(false);
  const geocodeZipFn = useServerFn(geocodeZip);
  const [loadingShared, setLoadingShared] = useState<boolean>(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [showShare, setShowShare] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [shareEmail, setShareEmail] = useState({ from: "", to: "" });
  const [saveEmail, setSaveEmail] = useState("");
  const [sharing, setSharing] = useState(false);
  const [saving, setSaving] = useState(false);
  const savePlanFn = useServerFn(savePlan);
  const upsertMyPlanFn = useServerFn(upsertMyPlan);
  const sharePlanFn = useServerFn(sharePlan);
  const getPlanByTokenFn = useServerFn(getPlanByToken);
  const submitPlanRatingFn = useServerFn(submitPlanRating);
  const { user } = useAuth();
  const [rated, setRated] = useState<boolean>(false);
  const [ratingChoice, setRatingChoice] = useState<"yes" | "no" | null>(null);
  const [ratingFeedbackId, setRatingFeedbackId] = useState<string | null>(null);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [submittingRating, setSubmittingRating] = useState<"yes" | "no" | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (sessionStorage.getItem("rp-plan-rated") === "1") setRated(true);
    }
  }, []);

  const handleRate = async (rating: "yes" | "no") => {
    if (rated || submittingRating) return;
    setSubmittingRating(rating);
    try {
      const wizardRaw = typeof window !== "undefined" ? sessionStorage.getItem("rp-wizard") : null;
      const result = await submitPlanRatingFn({
        data: {
          rating,
          eventIds: events.map((e) => e.id),
          wizardData: wizardRaw ? JSON.parse(wizardRaw) : null,
        },
      });
      if (!result.ok) {
        toast.error(result.error || "Could not record rating.");
        setSubmittingRating(null);
        return;
      }
      setRatingChoice(rating);
      setRatingFeedbackId(result.feedbackId);
      setRated(true);
      if (typeof window !== "undefined") sessionStorage.setItem("rp-plan-rated", "1");
      setRatingModalOpen(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmittingRating(null);
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("plan");
      if (token) {
        setLoadingShared(true);
        (async () => {
          try {
            const res = await getPlanByTokenFn({ data: { token } });
            if (res.ok && Array.isArray(res.events) && res.events.length > 0) {
              const mapped = res.events.reduce<SavedEvent[]>((list, e) => {
                if (!e) return list;
                list.push({
                  id: e.id,
                  title: e.title,
                  description: e.description,
                  category: e.category,
                  venue_name: e.venue_name,
                  venue_address: e.venue_address,
                  map_url: e.map_url,
                  estimated_duration: e.estimated_duration,
                  cost_level: e.cost_level,
                  cost_details: e.cost_details,
                  drive_minutes_from_lwr: e.drive_minutes_from_lwr,
                  source_url: e.source_url,
                  schedule_notes: e.schedule_notes,
                  start_date: e.start_date,
                  end_date: e.end_date,
                  crowd_level: e.crowd_level,
                  weather_sensitivity: e.weather_sensitivity,
                  indoor_outdoor: e.indoor_outdoor,
                });
                return list;
              }, []);
              setEvents(mapped);
              sessionStorage.setItem("rp-itinerary", JSON.stringify(mapped));
              setLoadingShared(false);
              return;
            }
            toast.error("That shared plan isn't available anymore.");
          } catch {
            toast.error("Couldn't load that shared plan.");
          }
          setLoadingShared(false);
        })();
        return;
      }
      const stored = sessionStorage.getItem("rp-itinerary");
      if (stored) {
        try { setEvents(JSON.parse(stored)); } catch { /* noop */ }
      }
    }
  }, [getPlanByTokenFn]);

  const persist = useCallback((list: SavedEvent[]) => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("rp-itinerary", JSON.stringify(list));
    }
  }, []);

  // Prefetch ZIP centroids for any event without lat/lng so the optimizer's
  // distance math reflects the user's actual origin. Mirrors Explore's
  // pattern: bulk read from zip_cache, then geocode any misses.
  useEffect(() => {
    if (events.length === 0) return;
    const needed = new Set<string>();
    for (const e of events) {
      const zip = extractZipFromAddress(e.venue_address);
      if (zip) needed.add(zip);
    }
    if (needed.size === 0) return;
    let cancelled = false;
    (async () => {
      const next = new Map<string, LatLng>();
      const zips = [...needed];
      try {
        const { data } = await supabase
          .from("zip_cache")
          .select("zip, lat, lng")
          .in("zip", zips);
        if (data) for (const row of data) next.set(row.zip, { lat: row.lat, lng: row.lng });
      } catch { /* noop */ }
      if (!cancelled && next.size > 0) setZipCoords(new Map(next));
      const missing = zips.filter((z) => !next.has(z));
      if (missing.length > 0) {
        const results = await Promise.all(
          missing.map((zip) => geocodeZipFn({ data: { zip } }).catch(() => null)),
        );
        for (let i = 0; i < missing.length; i++) {
          const r = results[i];
          if (r && r.ok) next.set(missing[i], { lat: r.lat, lng: r.lng });
        }
        if (!cancelled) setZipCoords(new Map(next));
      }
    })();
    return () => { cancelled = true; };
  }, [events, geocodeZipFn]);

  const handleAutoArrange = () => {
    if (events.length === 0) return;
    setArranging(true);
    try {
      const wizardRaw = typeof window !== "undefined" ? sessionStorage.getItem("rp-wizard") : null;
      let origin: LatLng | null = null;
      let timeframe: { start?: string; end?: string } | null = null;
      if (wizardRaw) {
        try {
          const w = JSON.parse(wizardRaw);
          if (w?.origin?.lat && w?.origin?.lng) origin = { lat: w.origin.lat, lng: w.origin.lng };
          if (w?.timeframe?.start || w?.timeframe?.end) timeframe = w.timeframe;
        } catch { /* noop */ }
      }
      const result = optimizeItinerary(events, origin ?? LWR_CENTER, timeframe, zipCoords);
      setItinerary(result);
      const flat = flattenOrder(result);
      setEvents(flat);
      persist(flat);
      if (result.tightFit) {
        toast.warning("Tight fit — your trip window is shorter than ideal. Consider trimming or extending dates.");
      } else {
        toast.success(`Arranged into ${result.days.length} day${result.days.length > 1 ? "s" : ""}.`);
      }
    } catch {
      toast.error("Couldn't auto-arrange. Try again.");
    }
    setArranging(false);
  };

  // Drop the day grouping if the underlying events change (drag, remove)
  // — drive chips would otherwise misrepresent the new order.
  const clearItineraryOnEdit = () => { if (itinerary) setItinerary(null); };

  const removeEvent = (id: string) => {
    clearItineraryOnEdit();
    setEvents((prev) => {
      const next = prev.filter((e) => e.id !== id);
      persist(next);
      if (typeof window !== "undefined") {
        const savedRaw = sessionStorage.getItem("rp-saved");
        if (savedRaw) {
          try {
            const saved: string[] = JSON.parse(savedRaw);
            sessionStorage.setItem("rp-saved", JSON.stringify(saved.filter((s) => s !== id)));
          } catch { /* noop */ }
        }
      }
      return next;
    });
  };

  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => { e.preventDefault(); setOverIdx(idx); };
  const handleDrop = (idx: number) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setOverIdx(null); return; }
    clearItineraryOnEdit();
    setEvents((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      persist(next);
      return next;
    });
    setDragIdx(null);
    setOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };
  const handlePrint = () => { if (typeof window !== "undefined") window.print(); };

  const getGoogleMapsUrl = (event: SavedEvent) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue_name + " " + (event.venue_address || ""))}`;

  const handleShare = async () => {
    if (!shareEmail.to.trim()) return;
    if (!shareEmail.from.trim()) return;
    setSharing(true);
    try {
      const result = await sharePlanFn({
        data: {
          eventIds: events.map((e) => e.id),
          fromEmail: shareEmail.from.trim() || undefined,
          toEmail: shareEmail.to.trim(),
        },
      });
      if (!result.ok) {
        toast.error(result.error || "Could not share plan.");
        setSharing(false);
        setShowShare(false);
        return;
      }
      const shareUrl = `${SITE_URL}/plan?plan=${result.shareToken}`;
      const subject = "Check out my RanchPlanner itinerary! 🌴";
      const body = `Hey! I put together a fun plan using RanchPlanner:\n\n${events.map((e, i) => `${i + 1}. ${e.title} — ${e.venue_name}`).join("\n")}\n\nSee the full plan: ${shareUrl}\n\nMade with RanchPlanner — Venture Out. 🧡`;

      // Copy link to clipboard
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
      }

      toast.success("Share ready — opening your email draft.", { duration: 4000 });
      window.setTimeout(() => {
        window.location.href = `mailto:${shareEmail.to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      }, 150);
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSharing(false);
    setShowShare(false);
  };

  const handleSavePlan = async () => {
    if (!saveEmail.trim()) return;
    setSaving(true);
    try {
      const wizardRaw = typeof window !== "undefined" ? sessionStorage.getItem("rp-wizard") : null;
      const result = await savePlanFn({
        data: {
          eventIds: events.map((e) => e.id),
          wizardData: wizardRaw ? JSON.parse(wizardRaw) : null,
          saverEmail: saveEmail.trim(),
        },
      });
      if (!result.ok) {
        toast.error("Could not save plan. Please try again.");
        setSaving(false);
        setShowSave(false);
        return;
      }
      const shareUrl = `${SITE_URL}/plan?plan=${result.shareToken}`;

      toast.success(
        <div>
          <p className="font-semibold">Plan saved!</p>
          <p className="text-xs mt-1 text-muted-foreground">Bookmark this link to return: <a href={shareUrl} className="text-primary underline">{shareUrl}</a></p>
        </div>,
        { duration: 8000 }
      );
    } catch {
      toast.error("Could not save plan. Please try again.");
    }
    setSaving(false);
    setShowSave(false);
  };

  const handleSaveToAccount = async () => {
    if (saving || events.length === 0) return;
    setSaving(true);
    try {
      const wizardRaw = typeof window !== "undefined" ? sessionStorage.getItem("rp-wizard") : null;
      const result = await upsertMyPlanFn({
        data: {
          eventIds: events.map((e) => e.id),
          wizardData: wizardRaw ? JSON.parse(wizardRaw) : null,
        },
      });
      if (!result.ok) {
        toast.error("Could not save plan. Please try again.");
      } else {
        toast.success("Plan saved to your account.", { duration: 4000 });
      }
    } catch {
      toast.error("Could not save plan. Please try again.");
    }
    setSaving(false);
  };


  if (loadingShared) {
    return (
      <div className="min-h-screen pt-6 md:pt-10 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-none" style={{ fontFamily: "'Syne', sans-serif" }}>
            Loading plan<span className="text-highlight">…</span>
          </h1>
          <div className="mt-8 space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-28 rounded-2xl border-2 border-border bg-muted/40 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="min-h-screen pt-6 md:pt-10 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-none" style={{ fontFamily: "'Syne', sans-serif" }}>
            Your Plan<span className="text-highlight">.</span>
          </h1>
          <p className="mt-1 text-xl md:text-2xl text-foreground" style={{ fontFamily: "'Caveat', cursive" }}>
            Save events from the explore page to build your list.
          </p>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="mt-8 p-10 rounded-3xl border-2 border-dashed border-border text-center" style={{ background: "var(--color-card)" }}>
            <div className="flex flex-col items-center gap-5">
              <div className="h-20 w-20 rounded-2xl bg-primary/15 flex items-center justify-center">
                <MapPin className="h-10 w-10 text-primary" />
              </div>
              <p className="text-muted-foreground text-lg">No activities saved yet</p>
              <Link to="/explore"
                className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-bold text-primary-foreground shadow-lg hover:shadow-xl transition-all hover:scale-105"
                style={{ background: "var(--color-primary)", fontFamily: "'Syne', sans-serif" }}>
                Browse Events <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-6 md:pt-10 pb-16 px-4 print:py-4 print:px-0">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-none" style={{ fontFamily: "'Syne', sans-serif" }}>
              Your Plan<span className="text-highlight">.</span>
            </h1>
            <p className="mt-1 text-xl md:text-2xl text-foreground" style={{ fontFamily: "'Caveat', cursive" }}>
              {events.length} activit{events.length > 1 ? "ies" : "y"} saved — drag to reorder.
            </p>
          </div>
        </div>

        {/* Plan actions */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="mb-5 print:hidden">
          <div className="flex flex-nowrap gap-2">
            <button onClick={handleAutoArrange} disabled={arranging || events.length < 2}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-highlight text-highlight-foreground hover:opacity-90 shadow-sm transition-all hover:scale-105 disabled:opacity-50">
              <Sparkles className="h-4 w-4" /> {arranging ? "Arranging…" : "Auto-arrange"}
            </button>
            <button onClick={() => { setShowShare(!showShare); if (!showShare) setShowSave(false); }}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold shadow-sm transition-all hover:scale-105 ${showShare ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary hover:bg-primary/25"}`}>
              <Share2 className="h-4 w-4" /> Share
            </button>
            <button
              onClick={() => {
                if (user) {
                  handleSaveToAccount();
                } else {
                  setShowSave(!showSave);
                  if (!showSave) setShowShare(false);
                }
              }}
              disabled={user ? saving : false}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold shadow-sm transition-all hover:scale-105 disabled:opacity-50 ${showSave && !user ? "bg-accent text-accent-foreground" : "bg-accent/15 text-accent hover:bg-accent/25"}`}>
              <Save className="h-4 w-4" /> {user && saving ? "Saving…" : "Save"}
            </button>
            <button onClick={handlePrint}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-muted text-foreground hover:bg-muted/80 shadow-sm transition-all hover:scale-105">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </motion.div>

        <FeedbackModal
          open={ratingModalOpen}
          onClose={() => setRatingModalOpen(false)}
          pageUrl="/plan"
          feedbackId={ratingFeedbackId}
          rating={ratingChoice}
          title={ratingChoice === "yes" ? "Thanks! How can we make it even better?" : "Sorry to hear that — what can we improve?"}
          subtitle="Tell us what worked, what didn't, or anything missing. Bug reports, feature ideas, missing events — all welcome."
        />

        {/* Share Panel */}
        {showShare && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
            className="mb-4 p-4 rounded-xl border border-border bg-card overflow-hidden print:hidden">
            <p className="text-sm font-bold mb-3" style={{ fontFamily: "'Syne', sans-serif" }}>Share Plan</p>
            <div className="space-y-2">
              <input type="email" placeholder="Your email *" aria-label="Your email" value={shareEmail.from}
                onChange={(e) => setShareEmail((s) => ({ ...s, from: e.target.value }))} required maxLength={255}
                className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20" />
              <input type="email" placeholder="Recipient's email *" aria-label="Recipient's email" value={shareEmail.to}
                onChange={(e) => setShareEmail((s) => ({ ...s, to: e.target.value }))} required maxLength={255}
                className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20" />
              <div className="flex gap-2">
                <button onClick={handleShare} disabled={sharing || !shareEmail.to.trim() || !shareEmail.from.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                  {sharing ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Save Panel */}
        {showSave && !user && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
            className="mb-4 p-4 rounded-xl border border-border bg-card overflow-hidden print:hidden">
            <p className="text-sm font-bold mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>Save Plan</p>
            <p className="text-xs text-muted-foreground mb-3">Enter your email to save this plan. You'll get a shareable link to return anytime.</p>
            <div className="flex gap-2">
              <input type="email" placeholder="Your email *" aria-label="Your email to save plan" value={saveEmail}
                onChange={(e) => setSaveEmail(e.target.value)} required maxLength={255}
                className="flex-1 rounded-lg border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20" />
              <button onClick={handleSavePlan} disabled={saving || !saveEmail.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </motion.div>
        )}

        <div className="space-y-3">
          {events.map((event, i) => {
            const daysLabel = getScheduleLabel(event);
            const cardBorderColors = [
              "var(--color-primary)",
              "var(--color-accent)",
              "var(--color-highlight)",
            ];
            const accentColor = cardBorderColors[i % 3];
            const costLabel = event.cost_details || event.cost_level || "Free";
            // Find this event's stop in the optimized itinerary (if any).
            let stop: { driveFromPrevMin: number; arriveMin: number; departMin: number } | null = null;
            let dayHeader: { label: string; dateISO?: string; weekday?: number; totalActiveMin: number; isFirstStop: boolean } | null = null;
            let driveHomeMin = 0;
            let isLastOfDay = false;
            if (itinerary) {
              outer: for (const day of itinerary.days) {
                for (let s = 0; s < day.stops.length; s++) {
                  if (day.stops[s].event.id === event.id) {
                    stop = day.stops[s];
                    if (s === 0) {
                      dayHeader = {
                        label: day.label,
                        dateISO: day.dateISO,
                        weekday: day.weekday,
                        totalActiveMin: day.totalActiveMin,
                        isFirstStop: true,
                      };
                    }
                    if (s === day.stops.length - 1) {
                      isLastOfDay = true;
                      driveHomeMin = day.driveHomeMin;
                    }
                    break outer;
                  }
                }
              }
            }
            return (
              <div key={event.id} className="space-y-3">
                {dayHeader && (
                  <div className="pt-2 first:pt-0">
                    <div className="flex items-baseline justify-between gap-3 pb-2 border-b-2 border-foreground/15">
                      <h2 className="text-xl md:text-2xl font-black tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
                        {dayHeader.label}
                        {dayHeader.dateISO && (
                          <span className="ml-2 text-base font-bold text-muted-foreground">
                            {formatDayHeader(dayHeader.dateISO)}
                          </span>
                        )}
                      </h2>
                      <span className="text-xs font-bold text-muted-foreground whitespace-nowrap">
                        ~{formatDuration(dayHeader.totalActiveMin)}
                      </span>
                    </div>
                  </div>
                )}
                {stop && stop.driveFromPrevMin > 0 && (
                  <div className="flex items-center gap-2 pl-4 text-xs font-bold text-muted-foreground">
                    <Car className="h-3.5 w-3.5" />
                    <span>{stop.driveFromPrevMin} min drive</span>
                    <span className="text-foreground/40">·</span>
                    <span>arrive {formatClock(stop.arriveMin)}</span>
                  </div>
                )}
                {stop && stop.driveFromPrevMin === 0 && (
                  <div className="flex items-center gap-2 pl-4 text-xs font-bold text-muted-foreground">
                    <span>Start {formatClock(stop.arriveMin)}</span>
                  </div>
                )}
                <div
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDrop={() => handleDrop(i)}
                onDragEnd={handleDragEnd}
                className={`transition-all ${dragIdx === i ? "opacity-40" : ""} ${overIdx === i && dragIdx !== i ? "border-t-2 border-primary" : ""}`}
              >
                <EventCard
                  event={event}
                  index={i}
                  accentColor={accentColor}
                  scheduleLabel={daysLabel || undefined}
                  costLabel={costLabel}
                  mode="remove"
                  showDragHandle
                  onRemove={() => removeEvent(event.id)}
                />
                </div>
                {isLastOfDay && driveHomeMin > 0 && (
                  <div className="flex items-center gap-2 pl-4 text-xs font-bold text-muted-foreground">
                    <Home className="h-3.5 w-3.5" />
                    <span>{driveHomeMin} min back to your start</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Plan rating — moved below results */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="mt-8 p-4 rounded-2xl border border-border bg-card print:hidden">
          {rated ? (
            <p className="text-sm font-semibold text-foreground text-center" style={{ fontFamily: "'Syne', sans-serif" }}>
              🧡 Thanks for the feedback!
            </p>
          ) : (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-sm font-bold text-foreground" style={{ fontFamily: "'Syne', sans-serif" }}>
                Was this site helpful in planning your adventure?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleRate("yes")}
                  disabled={!!submittingRating}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-highlight text-highlight-foreground hover:opacity-90 transition-all hover:scale-105 disabled:opacity-50"
                >
                  <ThumbsUp className="h-4 w-4" /> Yes, love it
                </button>
                <button
                  onClick={() => handleRate("no")}
                  disabled={!!submittingRating}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-muted text-foreground hover:bg-muted/80 transition-all hover:scale-105 disabled:opacity-50"
                >
                  <ThumbsDown className="h-4 w-4" /> Not quite
                </button>
              </div>
            </div>
          )}
        </motion.div>

        <div className="mt-8 text-center print:hidden">
          <Link to="/explore" className="text-sm font-medium text-primary hover:underline">← Add more activities</Link>
        </div>
      </div>
    </div>
  );
}
